"use client";

import { useEffect, useRef, useState } from "react";

import { cn } from "@unreserved/utils/cn";
import { ChevronDown } from "lucide-react";

import { Input } from "@/app/_components/shared/input";

export interface ExternalSuggestionItem<T = unknown> {
  id: string;
  displayLabel: string;
  data?: T;
}

export interface PropertyAddressInputProps<T = unknown> {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  externalSuggestions?: ExternalSuggestionItem<T>[];
  onSelectSuggestion?: (item: ExternalSuggestionItem<T>) => void;
  onDropdownOpenChange?: (open: boolean, suggestionCount: number) => void;
  scrollToBottomWhenEmpty?: boolean;
  onEndReached?: () => void;
  loadingMore?: boolean;
  hasMore?: boolean;
  loading?: boolean;
}

const baseInputClasses =
  "border-border text-typography-900 placeholder:text-typography-400 focus:ring-primary-500 h-9 w-full rounded border px-3 text-sm focus:ring-2 focus:outline-none";

const END_REACHED_THRESHOLD = 50;

export function PropertyAddressInput<T = unknown>({
  value = "",
  onChange,
  placeholder = "Search or select property",
  className,
  externalSuggestions,
  onSelectSuggestion,
  onDropdownOpenChange,
  scrollToBottomWhenEmpty = false,
  onEndReached,
  loadingMore = false,
  hasMore = true,
  loading = false,
}: PropertyAddressInputProps<T>) {
  const [inputValue, setInputValue] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const isLoadingMoreRef = useRef(false);
  const lastScrollHeightRef = useRef(0);
  const wasOpenRef = useRef(false);

  const suggestions = Array.isArray(externalSuggestions) ? externalSuggestions : [];

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (isOpen) {
      if (!wasOpenRef.current) {
        setActiveIndex(suggestions.length > 0 ? 0 : -1);
        wasOpenRef.current = true;
      } else {
        setActiveIndex((i) => {
          if (suggestions.length === 0) return -1;
          if (i < 0) return 0;
          if (i >= suggestions.length) return suggestions.length - 1;
          return i;
        });
      }
    } else {
      wasOpenRef.current = false;
    }
  }, [isOpen, suggestions.length]);

  useEffect(() => {
    onDropdownOpenChange?.(isOpen, suggestions.length);
  }, [isOpen, suggestions.length, onDropdownOpenChange]);

  useEffect(() => {
    isLoadingMoreRef.current = loadingMore;
  }, [loadingMore]);

  useEffect(() => {
    if (!isOpen) {
      lastScrollHeightRef.current = 0;
    }
  }, [isOpen]);

  useEffect(() => {
    if (scrollToBottomWhenEmpty && isOpen && suggestions.length === 0 && containerRef.current) {
      let el: HTMLElement | null = containerRef.current.parentElement;
      while (el) {
        const { overflowY } = getComputedStyle(el);
        const canScroll = el.scrollHeight > el.clientHeight;
        if (
          canScroll &&
          (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay")
        ) {
          el.scrollTop = el.scrollHeight;
          break;
        }
        el = el.parentElement;
      }
    }
  }, [scrollToBottomWhenEmpty, isOpen, suggestions.length]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const item = listRef.current.children[activeIndex] as HTMLElement;
      item?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setInputValue(next);
    onChange?.(next);
    if (!isOpen) setIsOpen(true);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    setActiveIndex(suggestions.length > 0 ? 0 : -1);
  };

  const handleChevronClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  const handleSelect = (item: ExternalSuggestionItem<T>) => {
    setInputValue(item.displayLabel);
    onChange?.(item.displayLabel);
    onSelectSuggestion?.(item);
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        e.preventDefault();
        setIsOpen(true);
        setActiveIndex(suggestions.length > 0 ? 0 : -1);
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
      setActiveIndex(-1);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i < suggestions.length - 1 ? i + 1 : 0));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i > 0 ? i - 1 : suggestions.length - 1));
      return;
    }
    if (e.key === "Enter" && activeIndex >= 0 && suggestions[activeIndex]) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex] as ExternalSuggestionItem<T>);
      return;
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLUListElement>) => {
    if (!onEndReached || hasMore !== true || isLoadingMoreRef.current || loading) {
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollTop <= 0) {
      return;
    }
    if (scrollHeight === lastScrollHeightRef.current) {
      return;
    }
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - END_REACHED_THRESHOLD;

    if (isNearBottom) {
      lastScrollHeightRef.current = scrollHeight;
      isLoadingMoreRef.current = true;
      onEndReached();
    }
  };

  return (
    <div className={cn("relative w-full", className)} ref={containerRef}>
      <div
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls="property-suggestions-list"
        className="border-border-400 focus-within:border-primary-500 focus-within:ring-primary-200 flex h-9 w-full cursor-text items-center overflow-hidden rounded border bg-white focus-within:ring-4 focus-within:outline-none">
        <Input
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          className={cn(
            baseInputClasses,
            "border-none! focus:ring-0 focus:outline-none focus-visible:ring-0 focus-visible:outline-none",
          )}
          autoComplete="off"
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={handleChevronClick}
          className="text-typography-500 flex shrink-0 items-center justify-center self-stretch px-2 hover:bg-none"
          aria-label={isOpen ? "Close list" : "Open list"}>
          <ChevronDown className={cn("size-4 shrink-0", isOpen && "rotate-180")} aria-hidden />
        </button>
      </div>
      {isOpen && (
        <ul
          id="property-suggestions-list"
          ref={listRef}
          role="listbox"
          className="border-border absolute top-full z-50 mt-1 max-h-60 w-full overflow-auto rounded border bg-white py-1 shadow-lg"
          style={{ overscrollBehavior: "contain" }}
          onScroll={onEndReached ? handleScroll : undefined}>
          {loading && suggestions.length === 0 ? (
            <li className="text-typography-500 px-3 py-2 text-center text-sm">Loading...</li>
          ) : suggestions.length > 0 ? (
            <>
              {suggestions.map((item, index) => (
                <li key={item.id} role="option" aria-selected={index === activeIndex}>
                  <button
                    type="button"
                    onClick={() => handleSelect(item)}
                    onMouseEnter={() => setActiveIndex(index)}
                    className={cn(
                      "text-typography-700 hover:bg-background-50 w-full cursor-pointer bg-white px-3 py-2 text-left text-sm transition-colors",
                      index === activeIndex && "bg-background-50",
                    )}>
                    <span className="font-medium">{item.displayLabel}</span>
                  </button>
                </li>
              ))}
              {loadingMore && (
                <li className="text-typography-500 px-3 py-2 text-center text-sm">
                  Loading more...
                </li>
              )}
            </>
          ) : (
            <li className="text-typography-500 px-3 py-2 text-sm">No matching properties found.</li>
          )}
        </ul>
      )}
    </div>
  );
}
